import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  StatusBar,
  ActivityIndicator,
  Platform,
  Dimensions,
  ScrollView,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../screens/ThemeContext';

// Import conditionnel pour WebView
let WebView;
if (Platform.OS !== 'web') {
  WebView = require('react-native-webview').default;
}

const { width, height } = Dimensions.get('window');

export default function PDFViewer({ route, navigation }) {
  const { uri, title } = route.params;
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [pdfContent, setPdfContent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (Platform.OS === 'web') {
      // Sur le web, on peut directement utiliser l'URL
      setPdfContent(uri);
      setLoading(false);
    } else {
      loadPdfContent();
    }
  }, [uri]);

  const loadPdfContent = async () => {
    try {
      setLoading(true);
      
      // Import conditionnel pour FileSystem
      const FileSystem = await import('expo-file-system');
      
      // Lire le fichier en base64
      const base64Content = await FileSystem.readAsStringAsync(uri, {
        encoding: FileSystem.EncodingType.Base64
      });
      
      setPdfContent(base64Content);
    } catch (error) {
      console.error('Erreur de chargement du PDF:', error);
      setError('Impossible de charger le document');
    } finally {
      setLoading(false);
    }
  };

  const generateHtml = () => {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=2.0">
          <style>
            body, html {
              margin: 0;
              padding: 0;
              width: 100%;
              height: 100vh;
              overflow: auto;
              background-color: ${theme.dark ? '#1a1a1a' : '#ffffff'};
            }
            #pdf-container {
              width: 100%;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: flex-start;
            }
            #pdf-viewer {
              width: 100%;
              height: 100%;
              border: none;
            }
          </style>
        </head>
        <body>
          <div id="pdf-container">
            <iframe 
              id="pdf-viewer"
              src="${Platform.OS === 'web' ? uri : `data:application/pdf;base64,${pdfContent}`}"
              type="application/pdf"
            ></iframe>
          </div>
        </body>
      </html>
    `;
  };

  // Composant pour le web
  const WebPDFViewer = () => (
    <View style={styles.webviewContainer}>
      <iframe
        src={uri}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          backgroundColor: theme.dark ? '#1a1a1a' : '#ffffff'
        }}
        title={title}
      />
    </View>
  );

  // Composant pour mobile
  const MobilePDFViewer = () => (
    <View style={styles.webviewContainer}>
      {loading ? (
        <View style={[styles.loadingContainer, { backgroundColor: theme.background }]}>
          <ActivityIndicator size="large" color={theme.primary} />
          <Text style={[styles.loadingText, { color: theme.text }]}>
            Chargement du document...
          </Text>
        </View>
      ) : error ? (
        <View style={[styles.errorContainer, { backgroundColor: theme.background }]}>
          <MaterialCommunityIcons name="file-pdf-box" size={64} color={theme.error} />
          <Text style={[styles.errorText, { color: theme.text }]}>{error}</Text>
          <TouchableOpacity
            style={[styles.retryButton, { backgroundColor: theme.primary }]}
            onPress={() => navigation.goBack()}
          >
            <Text style={[styles.retryButtonText, { color: '#fff' }]}>
              Retour
            </Text>
          </TouchableOpacity>
        </View>
      ) : pdfContent && WebView ? (
        <WebView
          source={{ html: generateHtml() }}
          style={styles.webview}
          onLoadEnd={() => setLoading(false)}
          onError={(syntheticEvent) => {
            const { nativeEvent } = syntheticEvent;
            console.warn('WebView error:', nativeEvent);
            setError('Impossible d\'afficher le document');
          }}
          originWhitelist={['*']}
          mixedContentMode="always"
          allowFileAccess={true}
          allowUniversalAccessFromFileURLs={true}
          allowFileAccessFromFileURLs={true}
          javaScriptEnabled={true}
          domStorageEnabled={true}
          scrollEnabled={true}
          bounces={false}
          androidLayerType="hardware"
        />
      ) : null}
    </View>
  );

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <StatusBar barStyle={theme.dark ? "light-content" : "dark-content"} />
      
      <View style={[styles.header, { backgroundColor: theme.surface }]}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.primary} />
        </TouchableOpacity>
        
        <Text style={[styles.title, { color: theme.text }]} numberOfLines={1}>
          {title}
        </Text>
      </View>

      {Platform.OS === 'web' ? <WebPDFViewer /> : <MobilePDFViewer />}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  backButton: {
    padding: 8,
  },
  title: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 16,
  },
  webviewContainer: {
    flex: 1,
  },
  webview: {
    flex: 1,
    width: width,
    height: height,
  },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 16,
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 20,
  },
  retryButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  retryButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});